import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { UserService } from '../../services/user/UserService';
import { Popup, TopCenter } from '../../utils/popups';
import { Button } from '../Button';
import { AbstractPopup, DismissalReason } from './AbstractPopup';
import { PopupCssPreset } from './Popup.styles';
function PrivateChatPopup({ anchorRef, hostUserId, reject, }) {
    const [message, setMessage] = useState('');
    const messagesEndRef = useRef(null);
    const previousMessageCountRef = useRef(0);
    // Get private chat messages with the host
    const privateChatMessages = UserService.getOutput().usePrivateChatMessagesFromUser(hostUserId);
    const permissionService = UserService.getNotificationInterface();
    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        const currentMessageCount = privateChatMessages.length;
        if (currentMessageCount > previousMessageCountRef.current) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
        previousMessageCountRef.current = currentMessageCount;
    });
    const handleSendMessage = () => {
        const trimmedMessage = message.trim();
        if (!trimmedMessage) {
            return;
        }
        // Send the private message to the host
        permissionService.notifySendPrivateMessage(hostUserId, trimmedMessage);
        // Clear the input
        setMessage('');
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };
    return (_jsx(PrivateChatPopupRoot, { reject: reject, anchorRef: anchorRef, translateFn: TopCenter, children: _jsxs(Container, { children: [_jsxs(Header, { children: [_jsx(Title, { children: "Private Chat" }), _jsx(CloseButton, { type: "button", onClick: () => {
                                reject(DismissalReason.CloseButton);
                            }, children: "\u2715" })] }), _jsx(Subtitle, { children: "Only you and the host can see this chat." }), _jsxs(MessagesContainer, { children: [privateChatMessages.map((chatMessage) => (_jsxs(MessageRow, { "$isSent": chatMessage.isSent ?? false, children: [_jsx(Avatar, { "$isSent": chatMessage.isSent ?? false, children: chatMessage.isSent ? 'You' : 'Host' }), _jsxs(MessageBubble, { children: [_jsxs(MessageSender, { children: [chatMessage.isSent ? 'You' : 'Host', ":"] }), ' ', chatMessage.message] })] }, `${chatMessage.senderId}-${chatMessage.timestamp}`))), _jsx("div", { ref: messagesEndRef })] }), _jsx(MessageInputContainer, { children: _jsx(MessageInput, { placeholder: "Message", value: message, onChange: (e) => setMessage(e.target.value), onKeyDown: handleKeyDown }) }), _jsx(SendButton, { type: "button", disabled: !message.trim(), onClick: handleSendMessage, children: "Send" })] }) }));
}
const PrivateChatPopupRoot = styled(AbstractPopup) `
    ${PopupCssPreset.Fullscreen};
    --Popup_Padding: 0;

    @media (min-width: 30rem) {
        ${PopupCssPreset.AnchoredTopCenter};
        --Popup_Padding: 0;
        --Popup_Width: 23.75rem;
        --Popup_BorderRadius: 1.5rem;
    }
`;
const Container = styled.div `
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    padding: 1.25rem;
    height: 100%;
    box-sizing: border-box;

    @media (min-width: 30rem) {
        height: auto;
    }
`;
const Header = styled.div `
    display: flex;
    align-items: center;
    justify-content: space-between;
`;
const Title = styled.h2 `
    font-family: 'Poppins', sans-serif;
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.5rem;
    color: #ffffff;
    margin: 0;
`;
const CloseButton = styled.button `
    background: rgba(231, 231, 231, 0.2);
    border: none;
    border-radius: 1.375rem;
    width: 1.75rem;
    height: 1.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-size: 0.875rem;
    cursor: pointer;
    padding: 0;
    transition: background-color 200ms;

    &:hover {
        background: rgba(231, 231, 231, 0.3);
    }
`;
const Subtitle = styled.p `
    font-family: 'Poppins', sans-serif;
    font-size: 0.8125rem;
    line-height: 1.25rem;
    color: #98989d;
    margin: 0;
`;
const MessagesContainer = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    height: 13.75rem;
    overflow-y: auto;
    padding-right: 0.25rem;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 0.25rem;
    }

    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 0.125rem;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 0.125rem;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.3);
    }
`;
const MessageRow = styled.div `
    display: flex;
    gap: 0.375rem;
    align-items: flex-start;
`;
const Avatar = styled.div `
    width: 2rem;
    height: 2rem;
    border-radius: 6.25rem;
    background: ${({ $isSent }) => ($isSent ? '#98989d' : '#ffffff')};
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    color: ${({ $isSent }) => ($isSent ? '#ffffff' : '#1e1e1e')};
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
`;
const MessageBubble = styled.div `
    background: rgba(152, 152, 157, 0.2);
    border-radius: 1rem;
    padding: 0.5rem 0.75rem;
    color: #ffffff;
    font-family: 'Poppins', sans-serif;
    font-size: 0.9375rem;
    line-height: 1.375rem;
    flex: 1;
    word-wrap: break-word;
`;
const MessageSender = styled.span `
    font-weight: 500;
`;
const MessageInputContainer = styled.div `
    display: flex;
    width: 100%;
`;
const MessageInput = styled.textarea `
    background: rgba(214, 214, 214, 0.45);
    mix-blend-mode: color-burn;
    border: 0.0625rem solid rgba(255, 255, 255, 0.2);
    border-radius: 0.75rem;
    color: #ffffff;
    font-family: 'Poppins', sans-serif;
    font-size: 0.9375rem;
    line-height: 1.375rem;
    padding: 0.75rem;
    width: 100%;
    min-height: 5.5rem;
    resize: none;
    outline: none;
    box-sizing: border-box;

    &::placeholder {
        color: #545454;
    }

    &:focus {
        border-color: rgba(255, 255, 255, 0.4);
    }
`;
const SendButton = styled(Button) `
    width: 100%;
    background: #ffffff !important;
    color: #1e1e1e !important;
    border: none !important;
    height: 2.75rem !important;
    font-family: 'Poppins', sans-serif;
    font-size: 0.9375rem;
    font-weight: 500;

    &:disabled {
        opacity: 0.5;
    }
`;
const privateChatPopup = Popup.create(PrivateChatPopup);
export async function showPrivateChatPopup({ anchorRef, hostUserId, onBeforeOpen, onDismiss, }) {
    return new Promise((resolve) => {
        onBeforeOpen?.();
        privateChatPopup
            .pop({
            anchorRef,
            hostUserId,
        })
            .catch(() => {
            // User dismissed the popup
        })
            .finally(() => {
            onDismiss?.();
            resolve();
        });
    });
}
